import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "./supabase";

// useWeeklyPlan — draft CRUD for the weekly AI meal planner.
//
// One weekly_plans row per (user, week_start) draft (partial unique
// index, migration 0287). The draft's `slots` jsonb mutates in place
// as the user swaps / rerolls / locks on the review screen; slot
// writes are debounced so drag-through edits coalesce (egress rule 4).
// COMMIT is handled by weeklyPlanCommit.js — this hook just flips the
// status via markCommitted.
//
// No realtime: drafts are private (self-only RLS) and single-writer.
//
// Returns:
//   { draft, loading,
//     createDraft({ weekStart, factors, household, slots }),
//     saveSlots(slots),          // debounced 400ms
//     saveFactors(patch),        // { factors?, household? } immediate
//     discardDraft(),
//     markCommitted() }

const SLOT_SAVE_DEBOUNCE_MS = 400;

export function useWeeklyPlan(userId, weekStartISO) {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const saveTimer = useRef(null);
  const pendingSlots = useRef(null);

  useEffect(() => {
    if (!userId || !weekStartISO) {
      setDraft(null);
      setLoading(false);
      return undefined;
    }
    let alive = true;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("weekly_plans")
        .select("*")
        .eq("user_id", userId)
        .eq("week_start", weekStartISO)
        .eq("status", "draft")
        .maybeSingle();
      if (!alive) return;
      if (error) console.warn("[weeklyPlan] load:", error.message);
      setDraft(data || null);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [userId, weekStartISO]);

  const createDraft = useCallback(async ({ weekStart, factors, household, slots }) => {
    if (!userId) return null;
    const row = {
      user_id: userId,
      week_start: weekStart || weekStartISO,
      factors: factors || {},
      household: household || { servings: 2 },
      slots: slots || [],
      status: "draft",
    };
    // Plain insert — ON CONFLICT can't target the partial unique
    // index through PostgREST (no WHERE clause in its inference), so
    // a concurrent-tab 23505 is handled by reading the winner back.
    const { data, error } = await supabase
      .from("weekly_plans")
      .insert(row)
      .select()
      .single();
    if (error) {
      if (error.code === "23505") {
        const { data: existing } = await supabase
          .from("weekly_plans")
          .select("*")
          .eq("user_id", userId)
          .eq("week_start", row.week_start)
          .eq("status", "draft")
          .maybeSingle();
        if (existing) {
          setDraft(existing);
          return existing;
        }
      }
      console.warn("[weeklyPlan] createDraft:", error.message);
      return null;
    }
    setDraft(data);
    return data;
  }, [userId, weekStartISO]);

  const flushSlots = useCallback(async (draftId) => {
    const slots = pendingSlots.current;
    pendingSlots.current = null;
    if (!draftId || !slots) return;
    const { error } = await supabase
      .from("weekly_plans")
      .update({ slots })
      .eq("id", draftId);
    if (error) console.warn("[weeklyPlan] saveSlots:", error.message);
  }, []);

  const saveSlots = useCallback((slots) => {
    setDraft((d) => (d ? { ...d, slots } : d));
    pendingSlots.current = slots;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      setDraft((d) => {
        if (d?.id) flushSlots(d.id);
        return d;
      });
    }, SLOT_SAVE_DEBOUNCE_MS);
  }, [flushSlots]);

  // Flush any pending slot write on unmount so a quick close doesn't
  // drop the user's last swap.
  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setDraft((d) => {
      if (d?.id && pendingSlots.current) flushSlots(d.id);
      return d;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveFactors = useCallback(async (patch = {}) => {
    let id = null;
    setDraft((d) => {
      if (!d) return d;
      id = d.id;
      return { ...d, ...patch };
    });
    if (!id) return;
    const dbPatch = {};
    if (patch.factors) dbPatch.factors = patch.factors;
    if (patch.household) dbPatch.household = patch.household;
    if (!Object.keys(dbPatch).length) return;
    const { error } = await supabase.from("weekly_plans").update(dbPatch).eq("id", id);
    if (error) console.warn("[weeklyPlan] saveFactors:", error.message);
  }, []);

  const discardDraft = useCallback(async () => {
    let id = null;
    setDraft((d) => { id = d?.id ?? null; return null; });
    if (!id) return;
    const { error } = await supabase
      .from("weekly_plans")
      .update({ status: "discarded" })
      .eq("id", id);
    if (error) console.warn("[weeklyPlan] discard:", error.message);
  }, []);

  const markCommitted = useCallback(async () => {
    let id = null;
    setDraft((d) => { id = d?.id ?? null; return d; });
    if (!id) return;
    const { error } = await supabase
      .from("weekly_plans")
      .update({ status: "committed", committed_at: new Date().toISOString() })
      .eq("id", id);
    if (error) console.warn("[weeklyPlan] markCommitted:", error.message);
    else setDraft((d) => (d ? { ...d, status: "committed" } : d));
  }, []);

  return { draft, loading, createDraft, saveSlots, saveFactors, discardDraft, markCommitted };
}
